import { useEffect, useRef, useState } from 'react'
import { Alert, Button, NumberInput, Select, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/fr'
import { futureSellingConclusion } from '@/lib/futureSellingConclusion'

dayjs.extend(relativeTime)
import Box from '@/components/ui/Box'
import useApiSWR from '@/hooks/useApiSWR'
import { useAxiosApi } from '@/hooks/useAxiosApi'

const sellingOptions = [
    { value: 'follow', label: 'Suivre la quantité' },
    { value: 'force_available', label: 'Continuer à vendre en cas de rupture' },
    { value: 'force_disabled', label: 'Rupture forcée' },
]
const b2cOptions = [...sellingOptions.slice(0, 2), { value: 'preorder', label: 'Précommande' }, sellingOptions[2]]
function formValues(data) {
    return {
        future_availability_b2c: data.future_availability_b2c,
        future_availability_b2b: data.future_availability_b2b,
        future_dispatch_date: data.future_dispatch_date ?? '',
        future_preorder_cap: data.future_preorder_cap ?? '',
    }
}

function changeLabel(change) {
    if (!change) return null
    const reason = change.metadata?.reason
    const details = reason === 'future_stockout' && change.field === 'future_availability_b2c'
        ? ' (bascule automatique à la rupture)'
        : reason === 'future_preorder_cap_reached' && change.field === 'future_availability_b2c'
            ? ' (plafond de précommande atteint)' : ''
    return `${dayjs(change.created_at).locale('fr').fromNow()} par ${change.user?.name ?? 'le BO'}${details}`
}

export default function ComponentFutureSelling({ componentId }) {
    const endpoint = `/components/${componentId}/future-selling`
    const { data, error, mutate } = useApiSWR(endpoint, null, { revalidateOnFocus: false, refreshInterval: 30000 })
    const { requestPatch } = useAxiosApi()
    const [draft, setDraft] = useState(null)
    const baseline = useRef(null)
    const [saving, setSaving] = useState(false)
    const [errors, setErrors] = useState({})

    useEffect(() => {
        if (data && (!draft || JSON.stringify(draft) === JSON.stringify(baseline.current?.values))) {
            baseline.current = { values: formValues(data), revision: data.revision }
            setDraft(formValues(data))
        }
    }, [data])

    const change = (field, value) => {
        setErrors({})
        setDraft((current) => ({ ...current, [field]: value }))
    }
    const changeMode = (value) => {
        setErrors({})
        setDraft((current) => ({
            ...current,
            future_availability_b2c: value,
            ...(value === 'follow' ? { future_dispatch_date: '', future_preorder_cap: '' } : {}),
            ...(value === 'preorder' && baseline.current?.values.future_availability_b2c !== 'preorder'
                ? { future_availability_b2b: 'force_disabled' } : {}),
        }))
    }

    const save = async (event) => {
        event.preventDefault()
        const nextErrors = {}
        const cap = draft.future_preorder_cap
        if (draft.future_availability_b2c === 'preorder' && !draft.future_dispatch_date) {
            nextErrors.future_dispatch_date = 'Obligatoire en Précommande.'
        }
        if (cap !== '' && (!Number.isInteger(Number(cap)) || Number(cap) <= 0 || Number(cap) > 2147483647)) {
            nextErrors.future_preorder_cap = 'Le plafond est un nombre de pièces, supérieur à 0 (ex. 50).'
        }
        setErrors(nextErrors)
        if (Object.keys(nextErrors).length) return
        setSaving(true)
        try {
            const response = await requestPatch(endpoint, {
                ...draft,
                revision: baseline.current.revision,
                future_dispatch_date: draft.future_dispatch_date || null,
                future_preorder_cap: cap === '' ? null : Number(cap),
            })
            baseline.current = { values: formValues(response.data), revision: response.data.revision }
            setDraft(formValues(response.data))
            await mutate(response.data, false)
            notifications.show({ title: 'Préparation prochaine version enregistrée', message: 'Les réglages de vente actuels restent inchangés.', color: 'green' })
        } catch (failure) {
            setErrors(Object.fromEntries(Object.entries(failure.response?.data?.errors ?? {}).map(([field, messages]) => [field, messages[0]])))
        } finally {
            setSaving(false)
        }
    }

    // Preview the backend transition without rewriting the submitted mode:
    // the server must still record the stockout as an automatic BO change.
    const pendingStockout = draft?.future_availability_b2c === 'follow'
        && !!draft.future_dispatch_date && data?.available_paris <= 0
    const mode = pendingStockout ? 'preorder' : draft?.future_availability_b2c
    const showDate = mode === 'follow' || mode === 'preorder'
    const showCap = mode === 'preorder' || (mode === 'follow' && !!draft?.future_dispatch_date)
    const returningToFollow = mode === 'follow' && baseline.current?.values.future_availability_b2c !== 'follow'
    const enteringPreorder = mode === 'preorder' && baseline.current?.values.future_availability_b2c !== 'preorder'
    const dirty = draft && data && JSON.stringify(draft) !== JSON.stringify(baseline.current?.values)
    const pastDate = showDate && draft?.future_dispatch_date && dayjs(draft.future_dispatch_date).isBefore(dayjs(), 'day')
    const lastB2CChange = data?.last_changes?.future_availability_b2c
    const lastB2BChange = data?.last_changes?.future_availability_b2b
    const sameMode = mode === data?.future_availability_b2c
    const conclusion = draft && data ? futureSellingConclusion({
        mode,
        date: draft.future_dispatch_date,
        cap: draft.future_preorder_cap,
        available: data.available_paris,
        count: enteringPreorder || mode === 'follow' ? 0 : data.preorder_count ?? 0,
        stockoutTransition: sameMode && lastB2CChange?.metadata?.reason === 'future_stockout',
        capTransition: sameMode && lastB2CChange?.metadata?.reason === 'future_preorder_cap_reached',
        b2bTransition: draft.future_availability_b2b === 'force_disabled'
            && data.future_availability_b2b === 'force_disabled'
            && ['future_stockout', 'future_preorder_cap_reached', 'future_preorder_entry'].includes(lastB2BChange?.metadata?.reason),
    }) : null

    return (
        <Box title="Préparation prochaine version">
            <div className="flex flex-col gap-5">
                <Alert color="blue">
                    Réglages en préparation, sans effet sur le site, Shopify ou le site revendeur.
                    Les commandes B2C depuis le début de la vague consomment le plafond. Les bascules automatiques modifient uniquement les réglages de cette prochaine version.
                </Alert>
                {error ? (
                    <div role="alert">Impossible de charger les réglages. <Button variant="subtle" onClick={() => mutate()}>Réessayer</Button></div>
                ) : !draft ? <p role="status">Chargement…</p> : (
                    <form onSubmit={save} className="flex flex-col gap-5">
                        <fieldset disabled={saving} className="flex flex-col gap-5 min-w-0 border-0 p-0 m-0">
                            <div className="grid grid-cols-1 @xl:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1">
                                    <Select label="Vente B2C" data={b2cOptions} value={mode} allowDeselect={false} onChange={changeMode} error={errors.future_availability_b2c} />
                                    {lastB2CChange && <span className="text-xs text-gray-500">{changeLabel(lastB2CChange)}</span>}
                                </div>
                                <div className="flex flex-col gap-1">
                                    <Select label="Vente B2B" data={sellingOptions} value={pendingStockout ? 'force_disabled' : draft.future_availability_b2b} allowDeselect={false}
                                        disabled={enteringPreorder} onChange={(value) => change('future_availability_b2b', value)} error={errors.future_availability_b2b} />
                                    {lastB2BChange && <span className="text-xs text-gray-500">{changeLabel(lastB2BChange)}</span>}
                                </div>
                            </div>
                            {showDate && (
                                <div className="border-t pt-4 flex flex-col gap-3">
                                    <span className="text-sm font-medium">Vente B2C</span>
                                    <div className="grid grid-cols-1 @xl:grid-cols-2 gap-4">
                                        <TextInput type="date" label="Date d’expédition" required={mode === 'preorder'} value={draft.future_dispatch_date}
                                            disabled={returningToFollow} error={errors.future_dispatch_date}
                                            onChange={(event) => {
                                                change('future_dispatch_date', event.target.value)
                                                if (!event.target.value && draft.future_availability_b2c === 'follow') change('future_preorder_cap', '')
                                            }}
                                            description={returningToFollow ? 'Vidée à chaque passage dans cette valeur.'
                                                : mode === 'preorder' ? 'Précommande immédiate, même avec du stock.' : draft.future_dispatch_date ? 'À 0 de disponible, le BO passe « Vente B2C » sur « Précommande » avec cette date.' : 'Vide : jour ouvré suivant, rupture à 0. Saisir une date prépare la précommande.'} />
                                        {showCap && <NumberInput label="Plafond de précommande" placeholder="Vide = sans limite" min={1} max={2147483647}
                                            allowDecimal={false} allowNegative={false} value={draft.future_preorder_cap} error={errors.future_preorder_cap}
                                            onChange={(value) => change('future_preorder_cap', value)}
                                            description={mode === 'preorder'
                                                ? draft.future_preorder_cap === ''
                                                    ? `${enteringPreorder ? 0 : data.preorder_count ?? 0} précommandes vendues · sans limite`
                                                    : `${enteringPreorder ? 0 : data.preorder_count ?? 0} précommandes sur ${draft.future_preorder_cap}`
                                                : 'Nombre total de pièces vendables en précommande après la bascule. Vide = sans limite.'} />}
                                    </div>
                                </div>
                            )}
                            {pastDate && <Alert color="orange">Date d'expédition dépassée : la vente continue à cette date. Mets-la à jour si besoin.</Alert>}
                            {conclusion && <div
                                role="status"
                                aria-live="polite"
                                className={`flex items-start gap-3 rounded-xl px-[14px] py-3 text-[14.5px] leading-normal ${
                                    { green: 'bg-[#ECFDF3] text-[#14532D]', orange: 'bg-[#FFF7E8] text-[#7C4A03]', red: 'bg-[#FEF2F2] text-[#7F1D1D]', gray: 'bg-[#F1F5F9] text-[#1E293B]' }[conclusion.color]
                                }`}
                            >
                                <strong className="font-semibold">{conclusion.title}</strong>
                                <span>{conclusion.text}</span>
                            </div>}
                            <div className="flex gap-3">
                                <Button type="submit" loading={saving} disabled={!dirty}>Enregistrer</Button>
                                {dirty && <Button variant="default" onClick={() => { baseline.current = { values: formValues(data), revision: data.revision }; setDraft(formValues(data)); setErrors({}) }}>Annuler</Button>}
                            </div>
                        </fieldset>
                    </form>
                )}

            </div>
        </Box>
    )
}
